'use client'

/**
 * Direct Token Deployment with Unique Source Code
 * 
 * This hook deploys tokens directly (not via factory) with unique
 * source code for easy verification on block explorers.
 */

import { useState } from 'react'
import { isTestnet, TESTNET_CHAIN_IDS, MAINNET_CHAIN_IDS } from '@/lib/contracts-config'

// Minimal ABI for the TokenDeployerProxy
export const DEPLOYER_PROXY_ABI = [
    {
        name: 'deployToken',
        type: 'function',
        stateMutability: 'payable',
        inputs: [{ name: 'bytecode', type: 'bytes' }],
        outputs: [{ name: 'tokenAddress', type: 'address' }],
    },
    {
        name: 'getCreationFee',
        type: 'function',
        stateMutability: 'view',
        inputs: [],
        outputs: [{ name: '', type: 'uint256' }],
    },
    {
        name: 'getCreationFeeInNative',
        type: 'function',
        stateMutability: 'view',
        inputs: [],
        outputs: [
            { name: 'fee', type: 'uint256' },
            { name: 'feeUsd', type: 'uint256' },
        ],
    },
] as const

/**
 * Get the TokenDeployerProxy address for a given chain ID
 */
export function getDeployerProxyAddress(chainId: number): string | null {
    if (isTestnet) {
        if (chainId === TESTNET_CHAIN_IDS.BSC_TESTNET) return process.env.NEXT_PUBLIC_DEPLOYER_PROXY_BSC_TESTNET || null
        if (chainId === TESTNET_CHAIN_IDS.SEPOLIA) return process.env.NEXT_PUBLIC_DEPLOYER_PROXY_SEPOLIA || null
        if (chainId === TESTNET_CHAIN_IDS.POLYGON_AMOY) return process.env.NEXT_PUBLIC_DEPLOYER_PROXY_POLYGON_AMOY || null
        if (chainId === TESTNET_CHAIN_IDS.BASE_SEPOLIA) return process.env.NEXT_PUBLIC_DEPLOYER_PROXY_BASE_SEPOLIA || null
    } else {
        if (chainId === MAINNET_CHAIN_IDS.BSC) return process.env.NEXT_PUBLIC_DEPLOYER_PROXY_BSC || null
        if (chainId === MAINNET_CHAIN_IDS.ETH) return process.env.NEXT_PUBLIC_DEPLOYER_PROXY_ETH || null
        if (chainId === MAINNET_CHAIN_IDS.POLYGON) return process.env.NEXT_PUBLIC_DEPLOYER_PROXY_POLYGON || null
        if (chainId === MAINNET_CHAIN_IDS.BASE) return process.env.NEXT_PUBLIC_DEPLOYER_PROXY_BASE || null
    }
    return null
}

export interface DeployUniqueTokenParams {
    // Basic
    name: string
    symbol: string
    decimals: number
    totalSupply: string  // Without decimals, e.g., "1000000000"
    ownerAddress: string
    chainId: number
    // Social (optional)
    website?: string
    telegram?: string
    twitter?: string
    // Features
    reflectionEnabled: boolean
    autoLiquidityEnabled: boolean
    isMintable: boolean
    isPausable: boolean
    // NEW: Security Features (V2)
    blacklistEnabled?: boolean
    tradeCooldownEnabled?: boolean
    tradeCooldownTime?: number  // in seconds
    // NEW: Early Sell Penalty
    earlySellPenaltyEnabled?: boolean
    earlySellPenaltyDuration?: number  // in seconds
    earlySellPenaltyTax?: number  // in basis points
    // NEW: Vesting/Lock
    vestingEnabled?: boolean
    vestingAmount?: string
    vestingReleaseTime?: number  // Unix timestamp
    vestingBeneficiary?: string
    // NEW: EIP-2612 Permit
    permitEnabled?: boolean
    // NEW: Staking Support
    stakingEnabled?: boolean
    // Tax (in basis points, 100 = 1%)
    buyTax: number
    sellTax: number
    transferTax: number
    reflectionFee: number
    liquidityFee: number
    // Anti-whale
    antiWhaleEnabled: boolean
    maxTxPercent: number      // e.g., 1 for 1%
    maxWalletPercent: number  // e.g., 2 for 2%
    // Wallets
    marketingWallet?: string
}

interface CompileResult {
    success: boolean
    bytecode?: string
    abi?: any[]
    sourceCode?: string
    sourceCodeHash?: string
    contractName?: string
    ipfsCid?: string
    ipfsUrl?: string
    error?: string
    message?: string
    details?: string[]
}

export function useDeployUniqueToken() {
    const [isCompiling, setIsCompiling] = useState(false)
    const [isDeploying, setIsDeploying] = useState(false)
    const [compileError, setCompileError] = useState<string | null>(null)
    const [deployError, setDeployError] = useState<string | null>(null)
    const [compiledData, setCompiledData] = useState<CompileResult | null>(null)
    const [deployedAddress, setDeployedAddress] = useState<string | null>(null)
    const [txHash, setTxHash] = useState<string | null>(null)

    /**
     * Step 1: Compile the unique source code
     */
    const compileToken = async (params: DeployUniqueTokenParams): Promise<CompileResult> => {
        setIsCompiling(true)
        setCompileError(null)

        try {
            // Convert percentages to raw amounts
            const totalSupplyNum = BigInt(params.totalSupply)
            const maxTxAmount = params.antiWhaleEnabled
                ? (totalSupplyNum * BigInt(Math.floor(params.maxTxPercent * 100))) / BigInt(10000)
                : totalSupplyNum
            const maxWalletAmount = params.antiWhaleEnabled
                ? (totalSupplyNum * BigInt(Math.floor(params.maxWalletPercent * 100))) / BigInt(10000)
                : totalSupplyNum

            const response = await fetch('/api/tokens/compile', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: params.name,
                    symbol: params.symbol,
                    decimals: params.decimals,
                    totalSupply: params.totalSupply,
                    ownerAddress: params.ownerAddress,
                    chainId: params.chainId,
                    website: params.website,
                    telegram: params.telegram,
                    twitter: params.twitter,
                    reflectionEnabled: params.reflectionEnabled,
                    autoLiquidityEnabled: params.autoLiquidityEnabled,
                    isMintable: params.isMintable,
                    isPausable: params.isPausable,
                    // V2 Security Features
                    blacklistEnabled: params.blacklistEnabled,
                    tradeCooldownEnabled: params.tradeCooldownEnabled,
                    tradeCooldownTime: params.tradeCooldownTime,
                    // Early Sell Penalty
                    earlySellPenaltyEnabled: params.earlySellPenaltyEnabled,
                    earlySellPenaltyDuration: params.earlySellPenaltyDuration,
                    earlySellPenaltyTax: params.earlySellPenaltyTax,
                    // Vesting
                    vestingEnabled: params.vestingEnabled,
                    vestingAmount: params.vestingAmount,
                    vestingReleaseTime: params.vestingReleaseTime,
                    vestingBeneficiary: params.vestingBeneficiary,
                    // Permit & Staking
                    permitEnabled: params.permitEnabled,
                    stakingEnabled: params.stakingEnabled,
                    // Taxes
                    buyTax: params.buyTax,
                    sellTax: params.sellTax,
                    transferTax: params.transferTax,
                    reflectionFee: params.reflectionFee,
                    liquidityFee: params.liquidityFee,
                    antiWhaleEnabled: params.antiWhaleEnabled,
                    maxTxAmount: maxTxAmount.toString(),
                    maxWalletAmount: maxWalletAmount.toString(),
                    marketingWallet: params.marketingWallet,
                })
            })

            const result = await response.json()

            if (!response.ok || !result.success) {
                const error = result.error || 'Compilation failed'
                setCompileError(error)
                return { success: false, error, details: result.details }
            }

            setCompiledData(result)
            return result
        } catch (err: any) {
            const error = err.message || 'Failed to compile token'
            setCompileError(error)
            return { success: false, error }
        } finally {
            setIsCompiling(false)
        }
    }

    /**
     * Step 2: Deploy the compiled bytecode using the DeployerProxy (Single Transaction)
     * This combines the platform fee and contract deployment into one transaction
     */
    const deployToken = async (
        walletClient: any,
        publicClient: any,
        account: string,
        customFee?: string,
        overrideCompiledData?: CompileResult
    ): Promise<{ address: string; hash: string } | null> => {
        // Use override if provided, otherwise use state
        const dataToDeploy = overrideCompiledData || compiledData

        if (!dataToDeploy?.bytecode || !dataToDeploy?.abi) {
            setDeployError('No compiled data available. Call compileToken first.')
            return null
        }

        setIsDeploying(true)
        setDeployError(null)

        try {
            // 1. Get current chain ID to select proxy
            const chainId = await walletClient.getChainId()

            // 2. Identify Proxy address using centralized helper
            const proxyAddress = getDeployerProxyAddress(chainId)
            if (!proxyAddress) {
                throw new Error(`Token deployment not supported on chain ${chainId}. No proxy found.`)
            }

            // 3. Get Fee from contract (dynamic, USD-based via Chainlink)
            let feeAmount: bigint
            if (customFee) {
                // If a custom fee is explicitly passed, use it
                feeAmount = BigInt(Math.floor(parseFloat(customFee) * 1e18))
            } else {
                // Read fee from the TokenDeployerProxy contract
                try {
                    const onChainFee = await publicClient.readContract({
                        address: proxyAddress as `0x${string}`,
                        abi: DEPLOYER_PROXY_ABI,
                        functionName: 'getCreationFee',
                    })
                    feeAmount = onChainFee as bigint
                    // Add 5% buffer to account for price fluctuations between read and tx execution
                    feeAmount = feeAmount + (feeAmount * BigInt(5)) / BigInt(100)
                } catch (readErr) {
                    console.warn('[Deploy] Could not read fee from contract, using fallback:', readErr)
                    feeAmount = BigInt(Math.floor(0.01 * 1e18)) // Fallback
                }
            }

            console.log(`[Deploy] Using Proxy at ${proxyAddress} on chain ${chainId}, Fee: ${feeAmount} wei`)
            console.log(`[Deploy] Bytecode length: ${dataToDeploy.bytecode?.length || 0} chars`)
            console.log(`[Deploy] Account: ${account}`)

            // 4. Call deployToken on Proxy
            console.log('[Deploy] Sending transaction request to wallet...')
            const hash = await walletClient.writeContract({
                account: account as `0x${string}`,
                address: proxyAddress as `0x${string}`,
                abi: DEPLOYER_PROXY_ABI,
                functionName: 'deployToken',
                args: [dataToDeploy.bytecode as `0x${string}`],
                value: feeAmount,
            })

            console.log('[Deploy] Transaction sent! Hash:', hash)
            setTxHash(hash)

            // 5. Wait for transaction receipt
            console.log('[Deploy] Waiting for transaction confirmation...')
            const receipt = await publicClient.waitForTransactionReceipt({ hash })

            // 6. Look for TokenDeployed event to get the NEW contract address
            // The proxy emits: event TokenDeployed(address indexed deployer, address indexed tokenAddress, uint256 fee);
            // We can also just look at the internal 'create' address if we can find it
            // but parsing the log is safer.

            // Minimal event ABI for parsing
            const eventAbi = {
                name: 'TokenDeployed',
                type: 'event',
                inputs: [
                    { name: 'deployer', type: 'address', indexed: true },
                    { name: 'tokenAddress', type: 'address', indexed: true },
                    { name: 'fee', type: 'uint256', indexed: false },
                ],
            } as const

            const deployLogs = receipt.logs.filter((log: any) =>
                log.address.toLowerCase() === proxyAddress.toLowerCase()
            )

            // Try to find the log
            let newTokenAddress = ''
            if (deployLogs && deployLogs.length > 0) {
                // In a real app we'd use viem's parseEventLogs, but we can search for the token address
                // Since tokenAddress is indexed, it's in the topics.
                // Topic 0: Keccak256("TokenDeployed(address,address,uint256)")
                // Topic 1: deployer (indexed)
                // Topic 2: tokenAddress (indexed)
                if (deployLogs[0].topics && deployLogs[0].topics.length >= 3) {
                    const topic2 = deployLogs[0].topics[2]
                    // Convert 32-byte topic to 20-byte address
                    newTokenAddress = `0x${topic2.slice(26)}`
                }
            }

            if (newTokenAddress) {
                console.log(`[Deploy] Successfully deployed token via proxy at: ${newTokenAddress}`)
                setDeployedAddress(newTokenAddress)
                return {
                    address: newTokenAddress,
                    hash: hash
                }
            } else {
                throw new Error('Failed to extract token address from proxy transaction logs')
            }
        } catch (err: any) {
            let error = err.message || 'Deployment failed'
            console.error('[Deploy] Error:', err)

            // Check for common WalletConnect issues
            if (error.includes('User rejected') || error.includes('rejected')) {
                error = 'Transaction was rejected in wallet'
            } else if (error.includes('timeout') || error.includes('Timeout')) {
                error = 'Wallet connection timed out. Please try reconnecting your wallet.'
            } else if (error.includes('session') || error.includes('Session')) {
                error = 'WalletConnect session issue. Please disconnect and reconnect your wallet.'
            } else if (dataToDeploy?.bytecode && dataToDeploy.bytecode.length > 100000) {
                // Large bytecode can cause WalletConnect issues
                error = `${error} (Note: Contract bytecode is large - ${Math.round(dataToDeploy.bytecode.length / 2000)}KB. Some wallets may have issues with large transactions)`
            }

            setDeployError(error)
            return null
        } finally {
            setIsDeploying(false)
        }
    }

    /**
     * Step 3: Save token to database with source code
     */
    const saveTokenToDatabase = async (
        address: string,
        txHash: string,
        params: DeployUniqueTokenParams,
        creatorWallet: string,
        overrideCompiledData?: CompileResult
    ): Promise<boolean> => {
        const dataToSave = overrideCompiledData || compiledData

        // Validate required fields
        if (!address || !txHash || !creatorWallet) {
            console.error('Missing required fields for save:', { address, txHash, creatorWallet })
            return false
        }

        try {
            const response = await fetch('/api/tokens', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    creatorWallet, // This is what the API expects
                    address,
                    name: params.name,
                    symbol: params.symbol,
                    decimals: params.decimals,
                    totalSupply: params.totalSupply, // Human-readable supply (decimals applied in smart contract)
                    chainId: params.chainId,
                    txHash,
                    isBurnable: true, // Always burnable
                    isMintable: params.isMintable,
                    isPausable: params.isPausable,
                    // Source code fields
                    sourceCode: dataToSave?.sourceCode,
                    sourceCodeHash: dataToSave?.sourceCodeHash,
                    ipfsCid: dataToSave?.ipfsCid,
                    ipfsUrl: dataToSave?.ipfsUrl,
                    compilerVersion: 'v0.8.33+commit.64118f21',
                    optimizationRuns: 200,
                    configJson: {
                        buyTax: params.buyTax,
                        sellTax: params.sellTax,
                        transferTax: params.transferTax,
                        reflectionEnabled: params.reflectionEnabled,
                        autoLiquidityEnabled: params.autoLiquidityEnabled,
                        antiWhaleEnabled: params.antiWhaleEnabled,
                        marketingWallet: params.marketingWallet,
                    },
                })
            })

            return response.ok
        } catch (err) {
            console.error('Failed to save token to database:', err)
            return false
        }
    }

    return {
        // State
        isCompiling,
        isDeploying,
        compileError,
        deployError,
        compiledData,
        deployedAddress,
        txHash,
        // Actions
        compileToken,
        deployToken,
        saveTokenToDatabase,
        // Combined loading state
        isPending: isCompiling || isDeploying,
    }
}
